import { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@/routes/hooks';
import { useAuthContext } from '@/auth/hooks';
import { paths } from '@/routes/paths';
import { OrderTypes, ColumnsOrder, IOrderTableFilters, ChoicesOrderStatus } from '@/types/order';
import { OrionModule, OrionAccess, OrionSubmodule } from '@/types';
import { ApiService } from '@/services/api.service';
import { t } from 'i18next';
import type { FilterField } from '@/components/forms/filters-hub/types';
import { useListLayout } from './use-list-layout';
import { checkCanCancelOrder } from '@/sections/administrative/accounts/orders/utils/cancel-order.utils';

const getDefaultFilters = (): IOrderTableFilters => ({
  search: '',
  page: 0,
  pageSize: 100,
  organizations: [],
  owners: [],
  status: [
    {
      _id: ChoicesOrderStatus.pendingApproval,
      label: t('Pending Approval'),
    },
    {
      _id: ChoicesOrderStatus.pendingProvision,
      label: t('Pending Provision'),
    },
    {
      _id: ChoicesOrderStatus.provisioning,
      label: t('Provisioning'),
    },
    {
      _id: ChoicesOrderStatus.failedProvision,
      label: t('Failed Provision'),
    },
    {
      _id: ChoicesOrderStatus.created,
      label: t('Draft'),
    },
  ],
  sort: { order: 'asc', orderBy: '' },
  excludeCreatedFromMarket: true,
  isMarket: false,
  salesOrderNumber: '',
  pendingBilling: false,
});

interface UseOrderListProps {
  organizationId?: string;
  orderType?: OrderTypes;
}

export function useOrderList({
  organizationId,
  orderType = OrderTypes.PURCHASE_ORDER,
}: UseOrderListProps = {}) {
  const router = useRouter();
  const { user } = useAuthContext();

  // Create default filters inside the hook to ensure i18next is initialized
  const defaultFilters = useMemo(() => getDefaultFilters(), []);

  const listLayout = useListLayout({
    entityName: 'order',
    defaultFilters,
  });

  const { filters, setFilters } = listLayout;

  // UI state
  const [openExport, setOpenExport] = useState(false);
  const [openAccountingExport, setOpenAccountingExport] = useState(false);

  // User permissions - memoized using OrionModule, OrionSubmodule, and OrionAccess constants
  const orderPermissions = useMemo(
    () => ({
      canCreate: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.ORDER,
        OrionAccess.CREATE
      ),
      canUpdate: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.ORDER,
        OrionAccess.UPDATE
      ),
      canDelete: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.ORDER,
        OrionAccess.DELETE
      ),
      canView: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.ORDER,
        OrionAccess.VIEW
      ),
      isAdmin: !!user?.isAdmin,
    }),
    [user]
  );

  // Get order-specific permissions using OrionModule, OrionSubmodule, and OrionAccess constants
  const getOrderPermissions = useCallback(
    (order: any) => ({
      canView: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.ORDER,
        OrionAccess.VIEW
      ),
      canUpdate: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.ORDER,
        OrionAccess.UPDATE
      ),
      canDelete: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.ORDER,
        OrionAccess.DELETE
      ),
      canUpdateItem: user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.ORDER,
        OrionAccess.UPDATE,
        order?.organization?.owner ?? ''
      ),
      canBill: order?.pendingBilling === true && order?.status === ChoicesOrderStatus.completed,
      isAdmin: !!user?.isAdmin,
      canCancel: checkCanCancelOrder(user, order.status as ChoicesOrderStatus),
    }),
    [user]
  );

  // Memoize canReset calculation
  const canReset = useMemo(
    () =>
      filters.organizations.length > 0 ||
      filters.owners.length > 0 ||
      filters.status.length > 0 ||
      (filters.search?.length ?? 0) > 0 ||
      (filters.salesOrderNumber?.length ?? 0) > 0,
    [filters]
  );

  // Handler to clear all filters
  const handleClearFilters = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      organizations: defaultFilters.organizations,
      owners: defaultFilters.owners,
      status: defaultFilters.status,
      isMarket: defaultFilters.isMarket,
      search: defaultFilters.search,
      salesOrderNumber: defaultFilters.salesOrderNumber,
      pendingBilling: defaultFilters.pendingBilling,
    }));
  }, [setFilters, defaultFilters]);

  // Filter fields configuration - matching OrderList exactly
  const fields: FilterField[] = useMemo(
    () => [
      ...(organizationId !== undefined
        ? []
        : [
            {
              type: 'autocomplete' as const,
              name: 'organizations',
              label: t('Organizations'),
              url: 'organizations/all',
            },
            {
              type: 'autocomplete' as const,
              name: 'owners',
              label: t('Sales Executive'),
              url: 'users/staff',
              showLogo: true,
              optionsMapper: (staffUsers: any) =>
                staffUsers.map((staff: any) => ({
                  ...staff,
                  label: `${staff.firstName} ${staff.lastName}`,
                })),
            },
          ]),
      {
        type: 'search-text' as const,
        name: 'salesOrderNumber',
        label: t('Sales Order Number'),
      },
      {
        type: 'autocomplete' as const,
        name: 'status',
        label: t('Status'),
        options: [
          {
            _id: ChoicesOrderStatus.created,
            label: t('Draft'),
          },
          {
            _id: ChoicesOrderStatus.pendingApproval,
            label: t('Pending Approval'),
          },
          {
            _id: ChoicesOrderStatus.pendingProvision,
            label: t('Pending Provision'),
          },
          {
            _id: ChoicesOrderStatus.provisioning,
            label: t('Provisioning'),
          },
          {
            _id: ChoicesOrderStatus.failedProvision,
            label: t('Failed Provision'),
          },
          {
            _id: ChoicesOrderStatus.completed,
            label: t('Completed'),
          },
          {
            _id: ChoicesOrderStatus.cancelled,
            label: t('Cancelled'),
          },
          {
            _id: ChoicesOrderStatus.rejected,
            label: t('Rejected'),
          },
        ],
      },
      {
        type: 'checkbox' as const,
        name: 'pendingBilling',
        label: t('Pending Billing'),
      },
      {
        type: 'checkbox' as const,
        name: 'isMarket',
        label: t('Marketplace'),
      },
    ],
    [organizationId]
  );

  // Table headers configuration
  const getTableHeaders = useCallback(
    (isUnderLargeScreen: boolean) => [
      {
        id: 'orderNumber',
        label: t('Order Nº'),
        align: 'left',
        width: '120px',
        sortable: true,
        sortKey: ColumnsOrder.orderNumber,
      },
      {
        id: 'company',
        label: t('Company'),
        align: 'left',
        width: '200px',
        sortable: true,
        sortKey: ColumnsOrder.company,
      },
      {
        id: 'salesOrderNumber',
        label: t('Sales Order Nº'),
        align: 'left',
        width: '120px',
        sortable: true,
        sortKey: 'salesOrderNumber',
      },
      ...(!isUnderLargeScreen
        ? [
            {
              id: 'items',
              label: t('Items'),
              align: 'center',
              width: '80px',
              sortable: true,
              sortKey: ColumnsOrder.items,
            },
          ]
        : []),
      {
        id: 'billingStatus',
        label: t('Billing Status'),
        align: 'center',
        width: '140px',
        sortable: true,
        sortKey: 'billingStatus',
      },
      {
        id: 'submittedBy',
        label: t('Submitted by'),
        align: 'left',
        width: '280px',
        sortable: true,
        sortKey: ColumnsOrder.submittedBy,
      },
      {
        id: 'status',
        label: t('Status'),
        align: 'center',
        width: '140px',
        sortable: true,
        sortKey: ColumnsOrder.status,
      },
      {
        id: 'tcv',
        label: `${t('TCV')} (USD)`,
        align: 'right',
        width: '120px',
        sortable: true,
        sortKey: 'tcv',
      },
      {
        id: 'margin',
        label: t('Margin'),
        align: 'right',
        width: '100px',
        sortable: true,
        sortKey: 'margin',
      },
      {
        id: 'lastModified',
        label: t('Last Modified'),
        align: 'right',
        width: '140px',
        sortable: true,
        sortKey: 'lastModified',
      },
      {
        id: '',
        label: '',
        align: 'right',
        width: '20px',
      },
    ],
    []
  );
  // Memoize data fetching function
  const fetchData = useCallback(async () => {
    const payload = {
      ...filters,
      page: listLayout.page + 1,
      pageSize: listLayout.rowsPerPage,
      ...(organizationId !== undefined
        ? { organizations: [organizationId] }
        : {
            organizations: filters.organizations.map((item: any) => item._id),
            owners: filters.owners.map((item: any) => item._id),
          }),
      status: filters.status.map((item: any) => item._id),
      isMarket: filters.isMarket,
      salesOrderNumber: filters.salesOrderNumber,
      pendingBilling: filters.pendingBilling,
      orderType,
    };

    return ApiService.getFilteredOrders(payload);
  }, [filters, listLayout.page, listLayout.rowsPerPage, organizationId, orderType]);

  const { data, refetch, isLoading } = useQuery(
    ['orderList', organizationId ?? '', filters, listLayout.page, listLayout.rowsPerPage],
    fetchData,
    { refetchOnMount: 'always' }
  );

  // Action handlers
  const handleEditRow = useCallback(
    (id: string) => {
      if (orderType === OrderTypes.BILLING_ORDER) {
        router.push(
          organizationId
            ? paths.management.accounts.organizations.tabEditBillingOrder(organizationId, id)
            : paths.management.accounts.billingOrders.edit(id)
        );
      } else {
        router.push(
          organizationId
            ? paths.management.accounts.organizations.tabEditOrder(organizationId, id)
            : paths.management.accounts.orders.edit(id)
        );
      }
    },
    [organizationId, router, orderType]
  );

  const handleDeleteRow = useCallback(
    async (id: string) => {
      await ApiService.deleteOrder(id);
      refetch();
    },
    [refetch]
  );

  const handleSortRequest = useCallback(
    (property: string) => {
      setFilters((prevState) => {
        const isAscending = prevState.sort.orderBy === property && prevState.sort.order === 'asc';
        return {
          ...prevState,
          page: 0,
          sort: {
            order: isAscending ? 'desc' : 'asc',
            orderBy: property,
          },
        };
      });
    },
    [setFilters]
  );

  // Custom reset function for orders that resets to exact defaultFilters
  const handleResetFilters = useCallback(() => {
    setFilters((prevState) => ({
      ...defaultFilters,
      status: [],
      search: (prevState as any).search || '',
    }));
  }, [setFilters, defaultFilters]);

  // Export handlers
  const handleOpenExport = useCallback(() => {
    setOpenExport(true);
  }, []);

  const handleCloseExport = useCallback(() => {
    setOpenExport(false);
  }, []);

  const handleOpenAccountingExport = useCallback(() => {
    setOpenAccountingExport(true);
  }, []);

  const handleCloseAccountingExport = useCallback(() => {
    setOpenAccountingExport(false);
  }, []);

  // Navigation handlers
  const handleBill = useCallback(
    (id: string) => {
      router.push(`${paths.management.accounts.orders.edit(id)}?bill`);
    },
    [router]
  );

  return {
    // List layout functionality (excluding handleResetFilters to use our custom one)
    ...listLayout,
    handleResetFilters,

    // Data
    data,
    refetch,
    canReset,
    isLoading,

    // UI state
    openExport,
    setOpenExport,
    openAccountingExport,
    setOpenAccountingExport,

    // User permissions
    orderPermissions,
    getOrderPermissions,
    // Backward compatibility
    userCanView: orderPermissions.canView,
    userCanUpdate: orderPermissions.canUpdate,
    userCanDelete: orderPermissions.canDelete,

    // UI helpers
    fields,
    getTableHeaders,

    // Action handlers
    handleEditRow,
    handleDeleteRow,
    handleSortRequest,
    handleClearFilters,
    handleOpenExport,
    handleCloseExport,
    handleOpenAccountingExport,
    handleCloseAccountingExport,
    handleBill,
  };
}
